import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { VariacionBenchmark, VariacionBenchmarkCreate, VariacionBenchmarkUpdate } from '@/modules/multichannel/types/configuracion'

interface VariacionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  variacion: VariacionBenchmark | null
  onSubmit: (data: VariacionBenchmarkCreate) => void
  onUpdate: (id: number, data: VariacionBenchmarkUpdate) => void
  isSubmitting: boolean
}

interface FormData {
  categoria: string
  rango_10_min: string
  rango_9_min: string
  rango_8_min: string
  rango_7_min: string
  rango_6_min: string
  rango_5_min: string
  rango_4_min: string
  rango_3_min: string
  rango_2_min: string
  peso_entrega: string
  peso_aperturas: string
  peso_clics: string
  calificacion_a_plus_min: string
  calificacion_a_min: string
  calificacion_b_min: string
  calificacion_c_min: string
}

const defaultValues: FormData = {
  categoria: 'General',
  rango_10_min: '110', rango_9_min: '105', rango_8_min: '100', rango_7_min: '95',
  rango_6_min: '90', rango_5_min: '85', rango_4_min: '80', rango_3_min: '70', rango_2_min: '60',
  peso_entrega: '40', peso_aperturas: '35', peso_clics: '25',
  calificacion_a_plus_min: '90', calificacion_a_min: '80', calificacion_b_min: '70', calificacion_c_min: '60',
}

export function VariacionFormDialog({
  open, onOpenChange, variacion, onSubmit, onUpdate, isSubmitting,
}: VariacionFormDialogProps) {
  const isEdit = !!variacion

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    defaultValues,
  })

  useEffect(() => {
    if (open && variacion) {
      reset({
        categoria: variacion.categoria,
        rango_10_min: String(variacion.rango_10_min), rango_9_min: String(variacion.rango_9_min),
        rango_8_min: String(variacion.rango_8_min), rango_7_min: String(variacion.rango_7_min),
        rango_6_min: String(variacion.rango_6_min), rango_5_min: String(variacion.rango_5_min),
        rango_4_min: String(variacion.rango_4_min), rango_3_min: String(variacion.rango_3_min),
        rango_2_min: String(variacion.rango_2_min),
        peso_entrega: String(variacion.peso_entrega), peso_aperturas: String(variacion.peso_aperturas),
        peso_clics: String(variacion.peso_clics),
        calificacion_a_plus_min: String(variacion.calificacion_a_plus_min),
        calificacion_a_min: String(variacion.calificacion_a_min),
        calificacion_b_min: String(variacion.calificacion_b_min),
        calificacion_c_min: String(variacion.calificacion_c_min),
      })
    } else if (!open) {
      reset(defaultValues)
    }
  }, [open, variacion, reset])

  const pesoEntrega = Number(watch('peso_entrega') || 0)
  const pesoAperturas = Number(watch('peso_aperturas') || 0)
  const pesoClics = Number(watch('peso_clics') || 0)
  const sumaPesos = pesoEntrega + pesoAperturas + pesoClics
  const pesosValidos = sumaPesos === 100

  const onFormSubmit = (data: FormData) => {
    if (!pesosValidos) return
    const payload = {
      categoria: data.categoria,
      rango_10_min: Number(data.rango_10_min), rango_9_min: Number(data.rango_9_min),
      rango_8_min: Number(data.rango_8_min), rango_7_min: Number(data.rango_7_min),
      rango_6_min: Number(data.rango_6_min), rango_5_min: Number(data.rango_5_min),
      rango_4_min: Number(data.rango_4_min), rango_3_min: Number(data.rango_3_min),
      rango_2_min: Number(data.rango_2_min),
      peso_entrega: Number(data.peso_entrega), peso_aperturas: Number(data.peso_aperturas),
      peso_clics: Number(data.peso_clics),
      calificacion_a_plus_min: Number(data.calificacion_a_plus_min),
      calificacion_a_min: Number(data.calificacion_a_min),
      calificacion_b_min: Number(data.calificacion_b_min),
      calificacion_c_min: Number(data.calificacion_c_min),
    }
    if (isEdit && variacion) {
      onUpdate(variacion.id, payload)
    } else {
      onSubmit(payload)
    }
  }

  const rangos = [
    { key: 'rango_10_min', label: '10' }, { key: 'rango_9_min', label: '9' },
    { key: 'rango_8_min', label: '8' }, { key: 'rango_7_min', label: '7' },
    { key: 'rango_6_min', label: '6' }, { key: 'rango_5_min', label: '5' },
    { key: 'rango_4_min', label: '4' }, { key: 'rango_3_min', label: '3' },
    { key: 'rango_2_min', label: '2' },
  ] as const

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Variacion' : 'Nueva Variacion'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifica los rangos, pesos y calificaciones.' : 'Define los rangos, pesos y calificaciones de benchmark.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="categoria">Categoria <span className="text-destructive">*</span></Label>
            <Input id="categoria" {...register('categoria', { required: 'Requerido' })} disabled={isSubmitting} />
            {errors.categoria && <p className="text-sm text-destructive">{errors.categoria.message}</p>}
          </div>

          {/* Rangos */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Rangos (% minimo para cada puntuacion)</Label>
            <div className="grid grid-cols-3 gap-3">
              {rangos.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <Label htmlFor={key} className="text-xs">Rango {label}</Label>
                  <Input id={key} type="number" step="0.01" {...register(key, { required: 'Requerido', min: 0 })} disabled={isSubmitting} />
                </div>
              ))}
            </div>
          </div>

          {/* Pesos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Pesos (deben sumar 100%)</Label>
              <span className={`text-sm font-medium ${pesosValidos ? 'text-green-600' : 'text-destructive'}`}>
                Suma: {sumaPesos}%
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="peso_entrega" className="text-xs">Entrega</Label>
                <Input id="peso_entrega" type="number" step="0.01" {...register('peso_entrega', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="peso_aperturas" className="text-xs">Aperturas</Label>
                <Input id="peso_aperturas" type="number" step="0.01" {...register('peso_aperturas', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="peso_clics" className="text-xs">Clics</Label>
                <Input id="peso_clics" type="number" step="0.01" {...register('peso_clics', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
            </div>
            {!pesosValidos && sumaPesos > 0 && (
              <p className="text-sm text-destructive">Los pesos deben sumar exactamente 100%</p>
            )}
          </div>

          {/* Calificaciones */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Calificaciones (puntuacion minima)</Label>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label htmlFor="calificacion_a_plus_min" className="text-xs">A+</Label>
                <Input id="calificacion_a_plus_min" type="number" {...register('calificacion_a_plus_min', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="calificacion_a_min" className="text-xs">A</Label>
                <Input id="calificacion_a_min" type="number" {...register('calificacion_a_min', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="calificacion_b_min" className="text-xs">B</Label>
                <Input id="calificacion_b_min" type="number" {...register('calificacion_b_min', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="calificacion_c_min" className="text-xs">C</Label>
                <Input id="calificacion_c_min" type="number" {...register('calificacion_c_min', { required: 'Requerido', min: 0, max: 100 })} disabled={isSubmitting} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || !pesosValidos}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
